import {
  INSIGHTS_CHANNELS,
  type InsightsChannel,
  type InsightsEntityType,
} from "@hightouch/lib/ad-studio/insights-channels/registry";
import { getEntityLabel } from "src/pages/ad-studio/insights/shared/channels";
import type {
  AccountOption,
  ChannelScope,
  ConnectedChannel,
  ConnectedSync,
  ConversionEvents,
  InsightsMeta,
  NonEmptyArray,
  SyncScope,
  ViewMeta,
} from "src/pages/ad-studio/insights/performance/types";

// Channels come from the shared registry, so the picker allowlist and labels
// stay in lockstep with what the backend resolvers support. Channels with a
// featureFlag stay hidden until the flag is on for the workspace.
export const supportedSourceTypesOf = (
  flags: Readonly<Record<string, unknown>>,
): Set<string> =>
  new Set(
    INSIGHTS_CHANNELS.filter(
      (channel) => !channel.featureFlag || flags[channel.featureFlag] === true,
    ).map((channel) => channel.sourceType),
  );

export const offersAccounts = ({
  view,
  channel,
}: {
  view: ViewMeta;
  channel: InsightsChannel;
}): boolean =>
  (
    view.channelsOf.get(view.controls.channelPicker.account.member) ?? []
  ).includes(channel.sourceType);

const toConnectedChannel = ({
  meta,
  channel,
  syncIds,
  accountsOf,
}: {
  meta: InsightsMeta;
  channel: InsightsChannel;
  syncIds: NonEmptyArray<string>;
  accountsOf: ReadonlyMap<string, readonly AccountOption[]>;
}): readonly ConnectedChannel[] => {
  if (!offersAccounts({ view: meta.views.ad, channel })) {
    return [{ channel, accounts: "by-sync", syncIds }];
  }
  const accountsOfSync = (syncId: string) => accountsOf.get(syncId) ?? [];
  const [firstSync, ...restSyncs] = syncIds.flatMap((syncId) => {
    const [account, ...others] = accountsOfSync(syncId);
    return account === undefined
      ? []
      : [{ syncId, accounts: [account, ...others] as const }];
  });
  return firstSync === undefined
    ? []
    : [
        {
          channel,
          accounts: "by-account",
          syncs: [firstSync, ...restSyncs],
          withoutAccounts: syncIds.filter(
            (syncId) => accountsOfSync(syncId).length === 0,
          ),
        },
      ];
};

type WholeSyncChannel = Extract<ConnectedChannel, { accounts: "by-sync" }>;

const registryIndexOf = (channel: InsightsChannel): number =>
  INSIGHTS_CHANNELS.findIndex(
    ({ sourceType }) => sourceType === channel.sourceType,
  );

const withWholeSync = (
  [head, ...tail]: NonEmptyArray<WholeSyncChannel>,
  { id, channel }: InsightsMeta["syncs"][number],
): NonEmptyArray<WholeSyncChannel> => {
  if (head.channel.sourceType === channel.sourceType) {
    return [{ ...head, syncIds: [...head.syncIds, id] }, ...tail];
  }
  const added: WholeSyncChannel = {
    channel,
    accounts: "by-sync",
    syncIds: [id],
  };
  if (registryIndexOf(channel) < registryIndexOf(head.channel)) {
    return [added, head, ...tail];
  }
  const [next, ...more] = tail;
  return next === undefined
    ? [head, added]
    : [head, ...withWholeSync([next, ...more], { id, channel })];
};

/** Each channel meta lists, in registry order, with its syncs whole, before any accounts load. */
export const wholeSyncsOf = ({
  syncs: [first, ...rest],
}: Pick<InsightsMeta, "syncs">): NonEmptyArray<WholeSyncChannel> =>
  rest.reduce(withWholeSync, [
    { channel: first.channel, accounts: "by-sync", syncIds: [first.id] },
  ]);

export function toConnectedChannels({
  meta,
  accountsOf,
}: {
  meta: InsightsMeta;
  accountsOf: ReadonlyMap<string, readonly AccountOption[]>;
}): readonly ConnectedChannel[] {
  return wholeSyncsOf(meta).flatMap(({ channel, syncIds }) =>
    toConnectedChannel({ meta, channel, syncIds, accountsOf }),
  );
}

export const syncIdsOf = (
  connected: ConnectedChannel,
): NonEmptyArray<string> => {
  switch (connected.accounts) {
    case "by-sync": {
      return connected.syncIds;
    }
    case "by-account": {
      const [first, ...rest] = connected.syncs;
      return [first.syncId, ...rest.map(({ syncId }) => syncId)];
    }
    default: {
      return connected satisfies never;
    }
  }
};

export function toEntityLabel({
  grain,
  scope,
}: {
  grain: InsightsEntityType;
  scope: NonEmptyArray<ChannelScope>;
}): string {
  const [only, ...others] = new Set(
    scope.map(({ channel }) => getEntityLabel(channel, grain)),
  );
  return only !== undefined && others.length === 0
    ? only
    : getEntityLabel(undefined, grain);
}

export const toEverySyncScope = (connected: ConnectedChannel): ChannelScope => {
  switch (connected.accounts) {
    case "by-sync": {
      const [first, ...rest] = connected.syncIds;
      const bySync = (syncId: string): SyncScope => ({
        kind: "by-sync",
        syncId,
      });
      return {
        channel: connected.channel,
        syncs: [bySync(first), ...rest.map(bySync)],
      };
    }
    case "by-account": {
      const [first, ...rest] = connected.syncs;
      const byAccount = ({ syncId, accounts }: ConnectedSync): SyncScope => {
        const [account, ...others] = accounts;
        return {
          kind: "by-account",
          syncId,
          accounts: [account.id, ...others.map(({ id }) => id)],
        };
      };
      return {
        channel: connected.channel,
        syncs: [byAccount(first), ...rest.map(byAccount)],
      };
    }
    default: {
      return connected satisfies never;
    }
  }
};

export const toEveryChannelScope = ([
  first,
  ...rest
]: NonEmptyArray<ConnectedChannel>): NonEmptyArray<ChannelScope> => [
  toEverySyncScope(first),
  ...rest.map(toEverySyncScope),
];

/** Whether the selection picks every connected sync whole: by sync, or every account the sync reports. */
/** Every channel the workspace has defaults for keeps its pick, or picks its defaults, so toggling it on always finds its events. */
export const withDefaultEvents = ({
  picked,
  defaults,
}: {
  picked: ConversionEvents;
  defaults: InsightsMeta["defaultEvents"];
}): ConversionEvents =>
  new Map([
    ...picked,
    ...[...defaults].filter(([sourceType]) => !picked.has(sourceType)),
  ]);

export const covers = ({
  scope,
  channels,
}: {
  scope: readonly ChannelScope[];
  channels: NonEmptyArray<ConnectedChannel>;
}): boolean => {
  const picked = scope.flatMap(({ syncs }) => syncs);
  return toEveryChannelScope(channels)
    .flatMap(({ syncs }) => syncs)
    .every((every) =>
      picked.some(
        (sync) =>
          sync.syncId === every.syncId &&
          (sync.kind === "by-sync" ||
            (every.kind === "by-account" &&
              every.accounts.every((account) =>
                sync.accounts.includes(account),
              ))),
      ),
    );
};

const accountCountsOf = (
  scope: readonly ChannelScope[],
): ReadonlyMap<string, number | "whole-sync"> =>
  new Map(
    scope.flatMap(({ syncs }) =>
      syncs.map((sync) => [
        sync.syncId,
        sync.kind === "by-sync" ? "whole-sync" : sync.accounts.length,
      ]),
    ),
  );

export const narrowsAccounts = ({
  scope,
  channels,
}: {
  scope: readonly ChannelScope[];
  channels: NonEmptyArray<ConnectedChannel>;
}): boolean => {
  const every = accountCountsOf(toEveryChannelScope(channels));
  return [...accountCountsOf(scope)].some(([syncId, count]) => {
    const all = every.get(syncId);
    return count !== "whole-sync" && all !== "whole-sync" && all !== count;
  });
};

// The list only shows rows its scope filter let through, so a row's sync is always in the scope.
export const syncScopeOf = ({
  scope,
  syncId,
}: {
  scope: readonly ChannelScope[];
  syncId: string;
}): SyncScope => {
  const sync = scope
    .flatMap(({ syncs }) => syncs)
    .find((candidate) => candidate.syncId === syncId);
  if (sync === undefined) {
    throw new Error(`Sync ${syncId} is not in the list's scope`);
  }
  return sync;
};
